import { useMemo } from 'react';
import { type MenuItem, type MenuSingleItem } from '@direct-frontend/components';
import { compact } from '@direct-frontend/stdlib';
import { type OpenApiSchemaProductVariant } from '#shared/api/schema';
import { openInNewTab } from '#shared/lib/router';
import { productVariantRoute } from '#shared/lib/routes';
import { checkIsLiteStore } from '#shared/lib/storeInfo';
import { addHttpsProtocol } from '#shared/lib/url';
import { Tooltip } from '#shared/ui/Tooltip';
import { productVariantMenuTestIds } from '../components/ProductVariantMenu/testids';
import {
    getProductVariantCheckoutUrl,
    PRODUCT_VARIANT_CHECKOUT_DISABLED_HINT,
} from '../lib/getProductVariantCheckoutUrl';
import { useProductsDeleteConfirm } from './useProductsDeleteConfirm';
import { useProductsPermanentDeleteConfirm } from './useProductsPermanentDeleteConfirm';
import { useProductVariantMenuArchiveAction } from './useProductVariantMenuArchiveAction';
import { useProductVariantMenuRecoverAction } from './useProductVariantMenuRecoverAction';
import { useProductVariantMenuStatusAction } from './useProductVariantMenuStatusAction';
import { useProductVariantsPermanentDelete } from './useProductVariantsPermanentDelete';

export interface ProductVariantMenuOptions {
    withEditLink?: boolean;
    onDeleted?: () => void;
    onStatusChanged?: (newStatus: OpenApiSchemaProductVariant['status']) => void;
}

function getOpenProductAction(
    variant: Pick<OpenApiSchemaProductVariant, 'status' | 'link_url' | 'stocks'>,
    isLite: boolean
): MenuSingleItem<string> | null {
    // В Lite при отсутствии variant.link_url мы не выкидываем null,
    // потому что в Lite мы выключаем кнопку, а не скрываем
    if (variant.status === 'ARCHIVED' || (!isLite && !variant.link_url)) {
        return null;
    }

    const url = isLite ? getProductVariantCheckoutUrl(variant) : addHttpsProtocol(variant.link_url);
    const content = isLite ? 'Открыть чекаут' : 'Показать на сайте';

    if (url) {
        return {
            key: 'on-site',
            content,
            url,
            target: '_blank',
        };
    }

    return {
        value: 'on-site',
        content,
        disabled: true,
        tooltipDescription: PRODUCT_VARIANT_CHECKOUT_DISABLED_HINT,
        tooltip: (props) => <Tooltip {...props} title={PRODUCT_VARIANT_CHECKOUT_DISABLED_HINT} maxWidth="360" />,
    };
}

export function useProductVariantMenu<
    V extends Pick<
        OpenApiSchemaProductVariant,
        'id' | 'product_id' | 'status' | 'link_url' | 'price' | 'stocks' | 'minimal_price'
    >,
>(variant: V | undefined, options: ProductVariantMenuOptions = {}) {
    const { id, product_id: productId, status } = variant ?? {};
    const { onDeleted, withEditLink = true, onStatusChanged } = options;
    const isLite = checkIsLiteStore();

    const { confirm: confirmDelete, ProductsDeleteModal } = useProductsDeleteConfirm();
    const { confirm: confirmPermanentDelete, ProductsPermanentDeleteModal } = useProductsPermanentDeleteConfirm(1);
    const { permanentDelete } = useProductVariantsPermanentDelete({
        query: id
            ? {
                  statuses: status ? [status] : [],
                  variant_ids: [id],
              }
            : {},
        confirmDelete: confirmPermanentDelete,
        onDelete: onDeleted,
    });

    const actionStatus = useProductVariantMenuStatusAction(variant, onStatusChanged);
    const actionRecover = useProductVariantMenuRecoverAction(id, onStatusChanged);
    const actionDelete = useProductVariantMenuArchiveAction(id, { confirmDelete, onDeleted });

    const items = useMemo<MenuItem[]>(() => {
        if (!variant || !id || !productId) {
            return [];
        }

        const editUrl = addHttpsProtocol(
            productVariantRoute.getUrl({
                params: {
                    variantId: id,
                    productId: productId,
                },
            })
        );

        return compact([
            {
                key: 'main',
                options: compact([
                    getOpenProductAction(variant, isLite),
                    status === 'ARCHIVED'
                        ? null
                        : {
                              value: 'status',
                              content: status === 'PUBLISHED' ? 'Скрыть' : 'Опубликовать',
                              onAction: actionStatus,
                          },
                    withEditLink && status !== 'ARCHIVED'
                        ? {
                              value: 'edit',
                              content: 'Редактировать',
                              onAction: () => {
                                  openInNewTab(editUrl);
                              },
                          }
                        : null,
                ]),
            },
            status === 'ARCHIVED'
                ? { value: 'recover', content: 'Восстановить из архива', onAction: actionRecover }
                : {
                      value: 'delete',
                      content: <span data-testid={productVariantMenuTestIds.archive}>Перенести в архив</span>,
                      onAction: actionDelete,
                  },
            status === 'ARCHIVED' ? { value: 'delete', content: 'Удалить', onAction: permanentDelete } : null,
        ]);
    }, [
        id,
        productId,
        variant,
        status,
        isLite,
        withEditLink,
        permanentDelete,
        actionStatus,
        actionRecover,
        actionDelete,
    ]);

    return {
        items,
        renderConfirmModal: () => (
            <>
                {ProductsDeleteModal}
                {ProductsPermanentDeleteModal}
            </>
        ),
    };
}
